import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
	ReadingRulerActiveAtomId,
	ReadingRulerColor,
	ReadingRulerMode,
	ReadingRulerOpacity,
	ReadingTintMode,
} from "../../state/accessibility";

import styled from "styled-components";
import { NavigationType } from "../../state/navigation";
import { PageIndex } from "../../state/page";

const OverlayContainer = styled.div<{ $zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${(props) => props.$zIndex};
  pointer-events: none; 
  overflow: hidden;
`;

const TintLayer = styled.div<{ $color: string }>`
    width: 100%;
    height: 100%;
    background-color: ${(props) => props.$color};
    transition: background-color 0.3s ease;
`;

// Uses a massive box-shadow to create the "dimmed" area around the cutout

const Cutout = styled.div<{
	$top: number;

	$left: number;

	$width: number;

	$height: number;

	$animate: boolean;

	$boxShadowColor: string;
}>`

  position: absolute;

  top: ${(props) => props.$top}px;

  left: ${(props) => props.$left}px;

  width: ${(props) => props.$width}px;

  height: ${(props) => props.$height}px;

  border-radius: 8px;

  transition: ${(props) => (props.$animate ? "all 0.3s ease-out" : "none")};

  box-shadow: 0 0 0 9999px ${(props) => props.$boxShadowColor}; 

`;

const hexToRgba = (hex: string, alpha: number) => {
	let r = 0;
	let g = 0;
	let b = 0;
	if (hex.length === 4) {
		r = Number.parseInt(hex[1] + hex[1], 16);
		g = Number.parseInt(hex[2] + hex[2], 16);
		b = Number.parseInt(hex[3] + hex[3], 16);
	} else if (hex.length === 7) {
		r = Number.parseInt(hex.slice(1, 3), 16);
		g = Number.parseInt(hex.slice(3, 5), 16);
		b = Number.parseInt(hex.slice(5, 7), 16);
	}
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Get elements sorted by visual position (top-to-bottom, left-to-right)
const getVisualOrderedAtomIds = (): number[] => {
	const targetClass = "top";
	const elements = document.querySelectorAll(`.${targetClass} [data-atom-id]`);

	const elementsWithPosition: { atomId: number; top: number; left: number; width: number; height: number }[] = [];

	elements.forEach((el) => {
		const atomIdStr = (el as HTMLElement).dataset.atomId;
		if (atomIdStr) {
			const atomId = Number.parseInt(atomIdStr, 10);
			if (!Number.isNaN(atomId)) {
				const rect = el.getBoundingClientRect();
				// Skip elements with zero dimensions (hidden/collapsed)
				if (rect.width > 0 && rect.height > 0) {
					elementsWithPosition.push({
						atomId,
						top: Math.round(rect.top),
						left: Math.round(rect.left),
						width: Math.round(rect.width),
						height: Math.round(rect.height),
					});
				}
			}
		}
	});

	console.log("[ReadingRuler] Found elements before sort:",
		elementsWithPosition.map(e => `atomId:${e.atomId} at (${e.top},${e.left}) ${e.width}x${e.height}`));

	// Sort by visual position: top-to-bottom, then left-to-right
	// Group elements into rows (within 20px vertical threshold)
	elementsWithPosition.sort((a, b) => {
		const rowThreshold = 20; // Elements within 20px are considered same row
		if (Math.abs(a.top - b.top) <= rowThreshold) {
			return a.left - b.left; // Same row: sort by left position
		}
		return a.top - b.top; // Different rows: sort by top position
	});

	console.log("[ReadingRuler] Elements after sort:",
		elementsWithPosition.map(e => `atomId:${e.atomId} at (${e.top},${e.left})`));

	return elementsWithPosition.map((e) => e.atomId);
};

const ReadingRulerOverlay = () => {
	const isRulerMode = useRecoilValue(ReadingRulerMode);
	const isTintMode = useRecoilValue(ReadingTintMode);
	const rulerColor = useRecoilValue(ReadingRulerColor);
	const rulerOpacity = useRecoilValue(ReadingRulerOpacity);
	const navigationType = useRecoilValue(NavigationType);

	const [activeAtomId, setActiveAtomId] = useRecoilState(
		ReadingRulerActiveAtomId,
	);

	const pageIndex = useRecoilValue(PageIndex);

	const [rect, setRect] = useState<{
		top: number;

		left: number;

		width: number;

		height: number;

		animate: boolean;
	} | null>(null);

	// Track visual order of elements for navigation
	const [visualOrder, setVisualOrder] = useState<number[]>([]);

	const showRuler = isRulerMode && navigationType === "tap";
	const showTint = isTintMode && !showRuler;

	// Keyboard focus tracking
	useEffect(() => {
		const handleFocus = (e: FocusEvent) => {
			if (!showRuler) return;
			const target = e.target as HTMLElement;
			if (target?.dataset.atomId) {
				const id = Number.parseInt(target.dataset.atomId, 10);
				if (!Number.isNaN(id)) {
					setActiveAtomId(id);
				}
			}
		};
		window.addEventListener("focusin", handleFocus);
		return () => window.removeEventListener("focusin", handleFocus);
	}, [showRuler, setActiveAtomId]);

	// Arrow key navigation - follows visual order
	useEffect(() => {
		if (!showRuler || visualOrder.length === 0) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			const currentIndex = visualOrder.indexOf(activeAtomId);
			if (currentIndex === -1) return;

			let nextIndex = currentIndex;

			if (e.key === "ArrowDown" || e.key === "ArrowRight") {
				e.preventDefault();
				nextIndex = Math.min(currentIndex + 1, visualOrder.length - 1);
			} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
				e.preventDefault();
				nextIndex = Math.max(currentIndex - 1, 0);
			}

			if (nextIndex !== currentIndex) {
				setActiveAtomId(visualOrder[nextIndex]);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [showRuler, visualOrder, activeAtomId, setActiveAtomId]);

	// Initialization Effect: Calculate visual order when ruler is enabled or page changes
	// Note: activeAtomId is NOT in dependencies to avoid feedback loops

	useEffect(() => {
		if (!showRuler) {
			setVisualOrder([]);
			return;
		}

		// Delay to allow DOM to render before calculating visual order
		const timeoutId = setTimeout(() => {
			const orderedIds = getVisualOrderedAtomIds();
			console.log("[ReadingRuler] Visual order calculated:", orderedIds);
			setVisualOrder(orderedIds);

			// Only set initial activeAtomId if it's not already valid
			// Use functional check to avoid stale closure
			setActiveAtomId((currentId) => {
				if (orderedIds.includes(currentId)) {
					console.log("[ReadingRuler] Current ID", currentId, "is valid, keeping it");
					return currentId; // Keep current if valid
				}
				if (orderedIds.length > 0) {
					console.log("[ReadingRuler] Setting initial ID to", orderedIds[0]);
					return orderedIds[0]; // Set to first if current is invalid
				}
				return currentId; // No change if no elements found
			});
		}, 200);

		return () => clearTimeout(timeoutId);
	}, [showRuler, pageIndex, setActiveAtomId]);

	useEffect(() => {
		if (!showRuler || activeAtomId === -1) {
			setRect(null);

			return;
		}

		const updateRect = (retries = 5) => {
			// When in ReadingRulerMode, DisplayElements forces PreviewElements which uses "top" position.

			const targetClass = "top";

			const selector = `.${targetClass} [data-atom-id="${activeAtomId}"]`;

			// Find the element by data-atom-id within the ACTIVE slide

			const element = document.querySelector(selector);

			if (element) {
				// Use instant scroll to avoid position mismatch during smooth animation
				element.scrollIntoView({ behavior: "instant", block: "center" });

				// Small delay to let scroll complete before measuring
				requestAnimationFrame(() => {
					const r = element.getBoundingClientRect();

					// Add a little padding
					const padding = 10;

					// Determine if we should animate (only if we already have a valid rect)
					// We use the functional update to check previous state securely
					setRect((prev) => ({
						top: r.top - padding,
						left: r.left - padding,
						width: r.width + padding * 2,
						height: r.height + padding * 2,
						animate: !!prev, // Animate only if we had a previous rect
					}));
				});
			} else {
				// If element not found, retry
				if (retries > 0) {
					setTimeout(() => updateRect(retries - 1), 100);
				} else {
					// Don't clear rect if element temporarily not found - keep last valid position
					console.warn(`[ReadingRuler] Element with atomId ${activeAtomId} not found after retries`);
				}
			}
		};

		const handleResizeOrScroll = () => updateRect();

		// Recalculate visual order on resize (orientation changes)
		const handleResize = () => {
			updateRect();
			// Delay visual order recalculation to allow layout to settle
			setTimeout(() => {
				const newOrder = getVisualOrderedAtomIds();
				setVisualOrder(newOrder);
			}, 200);
		};

		// Run initially with retries
		const timeoutId = setTimeout(() => updateRect(), 100);

		window.addEventListener("resize", handleResize);
		window.addEventListener("scroll", handleResizeOrScroll);

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("scroll", handleResizeOrScroll);
		};
	}, [showRuler, activeAtomId]);

	if (!showRuler && !showTint) return null;

	if (showTint) {
		return (
			<OverlayContainer $zIndex={103}>
				<TintLayer
					$color={hexToRgba(rulerColor || "#000000", rulerOpacity || 0.5)}
				/>
			</OverlayContainer>
		);
	}

	// Don't render ruler until we have a valid activeAtomId in the visual order
	// This prevents flashing when initial atomId (0) is a control character
	const isActiveIdValid = visualOrder.length > 0 && visualOrder.includes(activeAtomId);

	// If rect is not ready yet or activeAtomId is invalid, show full mask (cutout off-screen)
	// This prevents the screen from flashing bright (unmasked) during transitions.
	const renderRect = (rect && isActiveIdValid) ? rect : {
		top: -1000,
		left: -1000,
		width: 0,
		height: 0,
		animate: false,
	};

	return (
		<OverlayContainer $zIndex={101}>
			<Cutout
				$top={renderRect.top}
				$left={renderRect.left}
				$width={renderRect.width}
				$height={renderRect.height}
				$animate={renderRect.animate}
				$boxShadowColor={hexToRgba(
					rulerColor || "#000000",
					rulerOpacity || 0.85,
				)}
			/>
		</OverlayContainer>
	);
};
export default ReadingRulerOverlay;
